import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CoffeeForm } from "../components/CoffeeForm";
import { AiImportReview } from "../components/AiImportReview";
import { api, type ParseUsage } from "../lib/api";
import type { CoffeeInput } from "../types";
import { parseCsv } from "../lib/csv";
import { prepImageForUpload } from "../lib/image";

type Tab = "manual" | "ai" | "url" | "import";

export function AddCoffee() {
  const [tab, setTab] = useState<Tab>("manual");
  const nav = useNavigate();
  const [prefill, setPrefill] = useState<Partial<CoffeeInput> | null>(null);
  const [pendingPhotos, setPendingPhotos] = useState<File[]>([]);
  const [importRows, setImportRows] = useState<Partial<CoffeeInput>[] | null>(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<ParseUsage | null>(null);
  // Lifted up from AiTab so it survives tab switches — losing pasted text mid-parse
  // because of an accidental tab tap is the worst experience.
  const [aiText, setAiText] = useState("");
  const [urlInput, setUrlInput] = useState("");
  // Per-photo upload progress so the user sees "Uploading 1/2…" instead of a
  // motionless Saving… spinner that looks frozen on slow cellular.
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);

  // Tab switch does NOT reset in-flight state. If a AI parse is mid-flight or the
  // user has already pasted text / picked photos, they can switch tabs to peek at the
  // manual form without losing work. To wipe state, the user uses the Reset button on
  // the AI tab.
  const tabBtn = (id: Tab, label: string) => (
    <button
      onClick={() => { setTab(id); setError(null); }}
      className={tab === id ? "btn-primary" : "btn-ghost"}
    >
      {label}
    </button>
  );

  // Shared create: insert the row first (so the parsed text is saved no matter what),
  // then attempt photo uploads. Photo failures are non-fatal — surface them via alert
  // so the user can re-add photos later from the detail page rather than re-burning a
  // AI parse.
  const onCreate = async (input: CoffeeInput) => {
    const created = await api.create(input);
    const failures: string[] = [];
    if (pendingPhotos.length > 0) setUploadProgress({ done: 0, total: pendingPhotos.length });
    try {
      for (let i = 0; i < pendingPhotos.length; i++) {
        const file = pendingPhotos[i];
        try {
          await api.uploadPhoto(created.id, file);
        } catch (e) {
          failures.push(`#${i + 1} (${file.name}): ${(e as Error).message}`);
        }
        setUploadProgress({ done: i + 1, total: pendingPhotos.length });
      }
    } finally {
      setUploadProgress(null);
    }
    if (failures.length > 0) {
      alert(
        `Coffee saved, but ${failures.length} of ${pendingPhotos.length} photo(s) failed to upload:\n\n` +
        failures.join("\n") +
        `\n\nYou can add the photos from the coffee detail page.`,
      );
    }
    nav(`/coffee/${created.id}`);
  };

  // Combined AI parse: any mix of pasted text and photos goes to AI in a single
  // multi-modal request so producers who split info across bag + website end up in one
  // record. Either input is optional but at least one is required.
  const onAiParse = async (text: string, files: File[]) => {
    if (text.trim().length === 0 && files.length === 0) {
      setError("paste text or pick at least one photo");
      return;
    }
    setParsing(true); setError(null); setUsage(null);
    if (files.length > 6) setError(`only the first 6 of ${files.length} photos are sent`);
    try {
      const images = await Promise.all(files.slice(0, 6).map((f) => prepImageForUpload(f)));
      let parsed: Partial<CoffeeInput>;
      let u: ParseUsage | null = null;
      if (images.length > 0 && text.trim().length > 0) {
        ({ data: parsed, usage: u } = await api.parseTextAndImages(text, images));
      } else if (images.length > 0) {
        ({ data: parsed, usage: u } = await api.parseImages(images));
      } else {
        ({ data: parsed, usage: u } = await api.parseText(text));
      }
      setPrefill(parsed);
      setUsage(u);
    } catch (e) {
      setError((e as Error).message);
      setPrefill({});
    } finally {
      setParsing(false);
    }
  };

  const onUrlParse = async (url: string) => {
    if (!url.trim()) {
      setError("paste a roaster product URL");
      return;
    }
    setParsing(true); setError(null);
    try {
      const parsed = await api.parseUrl(url.trim());
      setPrefill(parsed);
    } catch (e) {
      setError((e as Error).message);
      setPrefill({});
    } finally {
      setParsing(false);
    }
  };

  const onImportFile = async (file: File) => {
    setParsing(true); setError(null);
    try {
      const text = await file.text();
      let rows: Partial<CoffeeInput>[] = [];
      try {
        const json = JSON.parse(text);
        if (Array.isArray(json)) rows = json;
      } catch { /* not JSON */ }
      if (rows.length === 0) {
        const grid = parseCsv(text);
        const lines = grid.map((r) => r.join(" — ").trim()).filter(Boolean);
        const parsedRows = await Promise.all(
          lines.map((line) => api.parseText(line).then((r) => r.data).catch(() => null))
        );
        rows = parsedRows.filter((r): r is Partial<CoffeeInput> => r != null);
      }
      if (rows.length === 0) throw new Error("no rows parsed from file");
      setImportRows(rows.map((r) => ({ portions: 10, grams: 15, ...r })));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setParsing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="font-display text-3xl">Add coffee</h2>
      <div className="flex flex-wrap gap-2">
        {tabBtn("manual", "Manual")}
        {tabBtn("ai", "Paste / Photo")}
        {tabBtn("url", "From URL")}
        {tabBtn("import", "Import file")}
      </div>

      {parsing ? (
        <div className="border border-paper-300 bg-paper-100 text-paper-800 px-3 py-2 text-sm flex items-center gap-3">
          <span className="inline-block w-3 h-3 border-2 border-ink border-t-transparent rounded-full animate-spin" />
          Parsing with AI… text + photo can take 10–30 s on mobile. Don't close the tab.
        </div>
      ) : null}
      {uploadProgress ? (
        <div className="border border-paper-300 bg-paper-100 text-paper-800 px-3 py-2 text-sm flex items-center gap-3">
          <span className="inline-block w-3 h-3 border-2 border-ink border-t-transparent rounded-full animate-spin" />
          Uploading photo {Math.min(uploadProgress.done + 1, uploadProgress.total)} of {uploadProgress.total}…
          phone uploads can take 10–30 s per photo on cellular.
        </div>
      ) : null}
      {error ? <div className="border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div> : null}
      {usage ? (
        <div className="text-[11px] uppercase tracking-widest text-paper-400">
          {usage.input.toLocaleString()} in · {usage.output.toLocaleString()} out{usage.reasoning ? ` · ${usage.reasoning} reasoning` : ""} · ≈{usage.cents}¢ · {usage.model}
        </div>
      ) : null}

      {/* Pending photos preview — shared by Manual / AI tabs */}
      {pendingPhotos.length > 0 && tab !== "import" ? (
        <PendingPhotos files={pendingPhotos} onChange={setPendingPhotos} />
      ) : null}

      {tab === "manual" ? (
        <div className="space-y-4">
          <ManualPhotoPicker files={pendingPhotos} onPick={(fs) => setPendingPhotos((cur) => [...cur, ...fs])} />
          <CoffeeForm submitLabel={pendingPhotos.length > 0 ? `Create + upload ${pendingPhotos.length} photo${pendingPhotos.length === 1 ? "" : "s"}` : "Create"} onSubmit={onCreate} />
        </div>
      ) : null}

      {tab === "ai" ? (
        <AiTab
          parsing={parsing}
          prefill={prefill}
          pendingPhotos={pendingPhotos}
          setPendingPhotos={setPendingPhotos}
          text={aiText}
          setText={setAiText}
          onParse={onAiParse}
          onCreate={onCreate}
          onReset={() => { setPrefill(null); setPendingPhotos([]); setAiText(""); }}
        />
      ) : null}

      {tab === "url" ? (
        <UrlTab
          parsing={parsing}
          prefill={prefill}
          url={urlInput}
          setUrl={setUrlInput}
          onParse={onUrlParse}
          onCreate={onCreate}
          onReset={() => { setPrefill(null); setUrlInput(""); }}
        />
      ) : null}

      {tab === "import" ? (
        importRows ? (
          <AiImportReview
            rows={importRows}
            onCancel={() => setImportRows(null)}
            onDone={(ids) => { nav(ids.length === 1 ? `/coffee/${ids[0]}` : "/browse"); }}
          />
        ) : (
          <div className="border border-paper-200 p-4 space-y-3">
            <p className="text-sm text-paper-700">
              Upload a <code>.json</code> array (matching the seed format) or a <code>.csv</code>/<code>.txt</code> file.
              Each row is sent through AI for structuring.
            </p>
            <input type="file" accept=".csv,.tsv,.txt,.json" onChange={(e) => e.target.files?.[0] && onImportFile(e.target.files[0])} />
            {parsing ? <p className="text-sm text-paper-600">Parsing…</p> : null}
          </div>
        )
      ) : null}
    </div>
  );
}

function PendingPhotos({ files, onChange }: { files: File[]; onChange: (next: File[]) => void }) {
  const removeAt = (i: number) => onChange(files.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      <div className="eyebrow">{files.length} photo{files.length === 1 ? "" : "s"} ready · uploaded after Save</div>
      <ul className="flex gap-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        {files.map((f, i) => (
          <li key={i} className="relative w-20 h-20 shrink-0 border border-paper-200 bg-paper-100 overflow-hidden">
            <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover"
              onLoad={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)} />
            {i === 0 ? (
              <span className="absolute top-1 left-1 text-[8pt] uppercase tracking-widest bg-ink text-paper-50 px-1">primary</span>
            ) : null}
            <button onClick={() => removeAt(i)} className="absolute top-0 right-0 w-5 h-5 bg-paper-50 border border-paper-300 text-xs">×</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ManualPhotoPicker({ files, onPick }: { files: File[]; onPick: (files: File[]) => void }) {
  return (
    <label className="btn-ghost cursor-pointer w-full sm:w-auto">
      {files.length > 0 ? "Add more photos" : "Attach photos (optional)"}
      <input type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => { if (e.target.files) onPick(Array.from(e.target.files)); e.target.value = ""; }} />
    </label>
  );
}

function AiTab({ parsing, prefill, pendingPhotos, setPendingPhotos, text, setText, onParse, onCreate, onReset }: {
  parsing: boolean;
  prefill: Partial<CoffeeInput> | null;
  pendingPhotos: File[];
  setPendingPhotos: React.Dispatch<React.SetStateAction<File[]>>;
  text: string;
  setText: (s: string) => void;
  onParse: (text: string, files: File[]) => void;
  onCreate: (input: CoffeeInput) => Promise<void>;
  onReset: () => void;
}) {
  if (prefill) {
    const photoCount = pendingPhotos.length;
    const summary = text.trim().length > 0 && photoCount > 0
      ? `AI merged your pasted text and ${photoCount} photo${photoCount === 1 ? "" : "s"}.`
      : photoCount > 0
      ? `AI read ${photoCount} photo${photoCount === 1 ? "" : "s"}.`
      : `AI read your pasted text.`;
    const submitLabel = photoCount > 0
      ? `Create + upload ${photoCount} photo${photoCount === 1 ? "" : "s"}`
      : "Create";
    return (
      <div className="space-y-3">
        <p className="text-sm text-paper-700">{summary} Review and edit, then save.</p>
        <CoffeeForm initial={prefill} submitLabel={submitLabel} onSubmit={onCreate} onCancel={onReset} />
      </div>
    );
  }
  const canParse = text.trim().length >= 10 || pendingPhotos.length > 0;
  return (
    <div className="border border-paper-200 p-4 space-y-3">
      <p className="text-sm text-paper-700">
        Paste any text from the roaster's site / description AND/OR pick photos of the bag
        (front, back, side). AI reads them all together — useful when some info is on
        the bag and the rest only on the website. Either input is optional, but pick at
        least one. Up to 6 photos per parse.
      </p>
      <textarea
        className="input-box min-h-[140px]"
        placeholder="Paste the roaster's product description here (optional)…"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <PhotoPicker
        count={pendingPhotos.length}
        onPick={(files) => setPendingPhotos((cur) => [...cur, ...files].slice(0, 6))}
      />
      {pendingPhotos.length > 0 ? (
        <div className="border border-paper-200 bg-paper-50 p-2 space-y-2">
          <div className="eyebrow">{pendingPhotos.length} photo{pendingPhotos.length === 1 ? "" : "s"} ready</div>
          <ul className="flex gap-2 overflow-x-auto">
            {pendingPhotos.map((f, i) => (
              <li key={i} className="relative w-16 h-16 shrink-0 border border-paper-200 bg-paper-100 overflow-hidden">
                <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover"
                  onLoad={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)} />
                <button
                  onClick={() => setPendingPhotos((cur) => cur.filter((_, idx) => idx !== i))}
                  className="absolute top-0 right-0 w-5 h-5 bg-paper-50 border border-paper-300 text-xs"
                >×</button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="flex gap-2">
        <button className="btn-primary" disabled={parsing || !canParse} onClick={() => onParse(text, pendingPhotos)}>
          {parsing ? "Parsing…" : "Parse with AI"}
        </button>
        {(text || pendingPhotos.length > 0) ? (
          <button type="button" className="btn-ghost" onClick={onReset} disabled={parsing}>Reset</button>
        ) : null}
      </div>
    </div>
  );
}

// iOS Safari has historically dropped the selection from a bare <input type="file">
// when its value is cleared programmatically right after a pick (the FileList becomes
// stale before React reads it). Defensive form: capture synchronously, defer the clear,
// and surface a visible result so the user always knows whether the pick registered.
function PhotoPicker({ count, onPick }: { count: number; onPick: (files: File[]) => void }) {
  return (
    <label className="inline-flex items-center gap-3 cursor-pointer btn-ghost">
      <span>{count > 0 ? "Add more photos" : "Choose photos"}</span>
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          const input = e.currentTarget;
          const list = input.files;
          if (!list || list.length === 0) return;
          const arr = Array.from(list).filter((f) => f.size > 0);
          if (arr.length > 0) onPick(arr);
          // Defer reset to next tick so iOS finishes processing the FileList first.
          setTimeout(() => { try { input.value = ""; } catch { /* ignore */ } }, 0);
        }}
      />
    </label>
  );
}

function UrlTab({ parsing, prefill, url, setUrl, onParse, onCreate, onReset }: {
  parsing: boolean;
  prefill: Partial<CoffeeInput> | null;
  url: string;
  setUrl: (s: string) => void;
  onParse: (url: string) => void;
  onCreate: (input: CoffeeInput) => Promise<void>;
  onReset: () => void;
}) {
  if (prefill) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-paper-700">AI fetched the page and parsed it. Review and edit, then save.</p>
        <CoffeeForm initial={prefill} submitLabel="Create" onSubmit={onCreate} onCancel={onReset} />
      </div>
    );
  }
  const valid = /^https?:\/\/\S+\.\S+/.test(url.trim());
  return (
    <div className="border border-paper-200 p-4 space-y-3">
      <p className="text-sm text-paper-700">
        Paste a roaster's product page URL. AI fetches the page itself and parses
        the coffee details — no scraping setup needed. Some sites that aggressively
        block bots may return little or no content.
      </p>
      <input
        type="url"
        className="input-box"
        placeholder="https://roaster.example/coffees/some-geisha"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
      />
      <div className="flex gap-2">
        <button className="btn-primary" disabled={parsing || !valid} onClick={() => onParse(url)}>
          {parsing ? "Fetching…" : "Fetch + Parse"}
        </button>
        {url ? (
          <button type="button" className="btn-ghost" onClick={onReset} disabled={parsing}>Reset</button>
        ) : null}
      </div>
    </div>
  );
}

